'use client';

import { useSyncExternalStore } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { UserButton, useAuth } from '@clerk/nextjs';
import { LogoIcon } from '@/components/shared/logo-icon';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useLanguage, useRTLUtils } from '@/lib/translation';
import { cn } from '@/lib/utils';
import { isSuperAdminViewClient } from '@/lib/permissions/client';
import { SuperAdminNavShortcuts } from '@/components/layout/super-admin-nav-shortcuts';
import { NavbarLocalhostBadge } from '@/components/layout/navbar-localhost-badge';

const navActionClassName =
  'inline-flex h-8 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md border px-2 text-sm font-medium transition-colors sm:px-3 text-orange-700 hover:text-orange-800 border-orange-200 hover:bg-orange-100 dark:text-orange-300 dark:hover:text-orange-200 dark:border-orange-900/50 dark:hover:bg-orange-900/20';

/** Same snapshot on server and first client paint — avoids hydration mismatch from i18n/RTL/Clerk. */
function useClientLayoutReady(): boolean {
  return useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );
}

export function AdminNavbar() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const router = useRouter();
  const isAdminHub = pathname === '/admin';
  const { currentLanguage } = useLanguage();
  const { isRTL } = useRTLUtils();
  const { isLoaded, isSignedIn, orgRole, sessionClaims } = useAuth();
  const isSuperAdmin = isSuperAdminViewClient(orgRole, sessionClaims);
  const layoutReady = useClientLayoutReady();
  const showSessionChrome = layoutReady && isLoaded && isSignedIn;
  const BackIcon = layoutReady && isRTL ? ArrowRight : ArrowLeft;

  return (
    <nav className="relative overflow-hidden bg-orange-50/80 dark:bg-orange-950/30 border-b border-orange-200 dark:border-orange-900/50 ltr">
      <div className="pointer-events-none absolute inset-y-[-64px] left-[-96px] w-[28rem] bg-gradient-to-br from-orange-300/90 via-orange-200/40 to-transparent blur-3xl dark:from-orange-500/60 dark:via-orange-500/20" />
      <div className="pointer-events-none absolute inset-y-[-64px] right-[-96px] w-[28rem] bg-gradient-to-bl from-orange-300/90 via-orange-200/40 to-transparent blur-3xl dark:from-orange-500/60 dark:via-orange-500/20" />
      <div className="relative z-10 w-full px-3 md:px-4">
        <div className="pointer-events-none absolute inset-x-0 top-1/2 z-20 hidden -translate-y-1/2 justify-center md:flex">
          {layoutReady ? <NavbarLocalhostBadge /> : null}
        </div>

        <div
          className={cn(
            'flex h-16 w-full min-w-0 items-center justify-between gap-2',
            layoutReady && !isRTL ? 'flex-row-reverse' : '',
          )}
        >
          <div
            className={cn(
              'flex min-w-0 shrink-0 items-center gap-4',
              layoutReady && currentLanguage.code === 'en' && 'md:flex-row-reverse',
            )}
          >
            <Link href="/chats" className="flex select-none items-center gap-2" aria-label="Kindway">
              <div className="flex h-8 w-8 items-center justify-center">
                <LogoIcon className="h-8 w-8" />
              </div>
              <span className="hidden text-xl font-bold leading-tight text-gray-900 dark:text-white md:block">
                Kindway
              </span>
            </Link>

            {showSessionChrome ? (
              <div className="flex items-center gap-3">
                <Link
                  href="/chats"
                  aria-label={t('navigation.adminMainScreen', 'Main screen')}
                  className={navActionClassName}
                >
                  <span className="text-sm">{t('navigation.adminMainScreen', 'Main screen')}</span>
                </Link>
                {isSuperAdmin ? <SuperAdminNavShortcuts theme="admin" /> : null}
              </div>
            ) : null}
          </div>

          {showSessionChrome ? (
            <div className="flex shrink-0 items-center gap-3">
              {!isAdminHub ? (
                <button
                  type="button"
                  onClick={() => router.back()}
                  aria-label={t('common.back', 'Back')}
                  className={navActionClassName}
                >
                  <BackIcon className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="hidden text-sm sm:inline">{t('common.back', 'Back')}</span>
                </button>
              ) : null}
              <UserButton
                userProfileMode="navigation"
                userProfileUrl="/settings"
                appearance={{
                  elements: {
                    avatarBox: 'w-8 h-8',
                  },
                }} />
            </div>
          ) : null}
        </div>
      </div>
    </nav>
  );
}
